import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { withTimeout } from "@/lib/withTimeout";

const CACHE_KEY = "installer_status_cache";
const TIMEOUT_MS = 8_000; // 8 seconds max for this bootstrap query

function getCached(): boolean | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw === "true") return true;
    // Never trust a cached incomplete state. Once platform setup is finished,
    // an old `false` value must not redirect visitors before the live check.
    if (raw === "false") localStorage.removeItem(CACHE_KEY);
  } catch {}
  return null;
}

function setCache(installed: boolean) {
  try {
    localStorage.setItem(CACHE_KEY, String(installed));
  } catch {}
}

async function fetchInstallerStatus(): Promise<{ installed: boolean }> {
  const { data, error } = await (supabase as any).rpc("get_installer_status");
  if (error) throw error;
  const installed = data === true;
  setCache(installed);
  return { installed };
}

export function useInstallerStatus() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["installer_status"],
    queryFn: () =>
      withTimeout(fetchInstallerStatus(), TIMEOUT_MS, "Installer status check timed out"),
    staleTime: 60 * 1000,
    retry: 1,
    retryDelay: 2000,
  });

  // If query failed or is still loading, use localStorage cache as fallback
  const cachedValue = getCached();
  const timedOut = !!error;

  return {
    installed: data?.installed ?? cachedValue ?? (timedOut ? true : null),
    loading: isLoading && !timedOut && cachedValue === null,
    error: timedOut,
    refetch,
  };
}
